import { MediaPlayer, type MediaPlayerInstance, MediaProvider } from "@vidstack/react";
import { DefaultVideoLayout, defaultLayoutIcons } from "@vidstack/react/player/layouts/default";
import clsx from "clsx";
import { Tabs } from "features/discover/Tabs";
import { ApprovalModal } from "features/videoUploads/ApprovalModal";
import { DeleteModal } from "features/videoUploads/DeleteModal";
import { GameSelectComponent } from "features/videoUploads/GameSelectComponent";
import {
	fileNotReadyReason,
	isFileReady,
	isVideoDeleted,
	isVideoPlaybackUnavailable,
	PLAYBACK_READY_TIMEOUT_MS,
	shouldStartPlaybackReadyTimeout,
} from "features/videoUploads/functions";
import { getVideoUploadErrorMessage } from "features/videoUploads/videoUploadErrors";
import { RenameVideoButton } from "features/videoUploads/RenameVideoButton";
import { RowActionButton, rowActionIcons } from "features/videoUploads/tables/rowActions";
import { _ } from "i18n";
import { VideoProviderName, type VideoUploadDetail } from "libs/apiClient";
import client from "libs/client";
import { useEffect, useMemo, useRef, useState } from "react";
import { getTableListBackUrl } from "features/datagrid/hooks/tableUrlState";
import { Link, useLocation, useSearchParams } from "react-router";
import Select, { type SingleValue } from "react-select";
import { useToast } from "ui-kit";
import Modal from "ui-kit/src/Modal";
import ToggleSwitch from "ui-kit/src/ToggleSwitch/ToggleSwitch";
import { MatchAndVideoInfoTab } from "./MatchAndVideoInfoTab";
import { MetadataForm } from "./form";
import {
	convertFormValuesToTimelineValues,
	convertMetadataToValues,
	convertValuesToPeriods,
	parseTime,
	providersDropdownOptions,
	timeToSeconds,
} from "./functions";
import { Timeline } from "./timeline";
import {
	type DefaultFormValues,
	type FormValues,
	MetadataSchema,
	type TimelineValues,
	VideoPageTab,
} from "./types";

function resolveVideoListBackUrl(location: ReturnType<typeof useLocation>): string {
	const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
	if (returnTo !== undefined && returnTo !== "") {
		return returnTo;
	}
	return getTableListBackUrl("/video-uploads/not-sent");
}

interface VideoMetadataEditorProps {
	video: VideoUploadDetail;
}

function isVideoPageTab(value: string | null): value is VideoPageTab {
	return value === VideoPageTab.Setup || value === VideoPageTab.Info;
}

export function VideoMetadataEditor({ video }: VideoMetadataEditorProps) {
	const [showGamesWithVideo, setShowGamesWithVideo] = useState(false);
	const { addSuccessToast, addErrorToast } = useToast();
	const [videoToApprove, setVideoToApprove] = useState<number>();
	const [showModal, setShowModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const location = useLocation();
	const videoListBackUrl = resolveVideoListBackUrl(location);
	const [searchParams, setSearchParams] = useSearchParams();
	const isVeoProvider = video.provider === VideoProviderName.Values.veo;
	const veoProviderOption = useMemo(
		() => ({ value: VideoProviderName.Values.veo, label: VideoProviderName.Values.veo }),
		[]
	);
	const [videoProvider, setVideoProvider] = useState<SingleValue<{ value: string; label: string }>>(
		isVeoProvider
			? veoProviderOption
			: video.provider
				? { value: video.provider, label: video.provider }
				: null
	);

	const activeTab: VideoPageTab = isVideoPageTab(searchParams.get("tab"))
		? (searchParams.get("tab") as VideoPageTab)
		: VideoPageTab.Setup;

	const metadata = MetadataSchema.nullable().parse(JSON.parse(video.metadata || "null"));
	let periods = {};
	if (metadata?.periods) {
		periods = convertMetadataToValues(metadata);
	}

	const [filledPeriods, setFilledPeriods] = useState<TimelineValues>(convertFormValuesToTimelineValues(periods) || {});
	const [currentTime, setCurrentTime] = useState(0);
	const [selectedGame, setSelectedGame] = useState({ game: metadata?.gameId, withVideo: false });

	const onGameSelect = (game: number) => {
		setSelectedGame({ game: game, withVideo: !!showGamesWithVideo });
	};

	const player = useRef<MediaPlayerInstance>(null);
	const [playbackFailed, setPlaybackFailed] = useState(false);
	const [playbackReady, setPlaybackReady] = useState(false);

	const setHalfTimes = client.addVideoMetadata.useMutation({
		onSuccess: () => {
			addSuccessToast("metadata updated");
			client.queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes("metadataVideos"),
			});
		},
		onError: (res) => addErrorToast(getVideoUploadErrorMessage(res)),
	});

	const onSubmit = (values: FormValues) => {
		const providerValue = isVeoProvider ? VideoProviderName.Values.veo : videoProvider?.value;
		if (selectedGame.game && video.id && providerValue) {
			setHalfTimes.mutate({
				id: video.id,
				body: {
					gameId: selectedGame.game,
					periods: convertValuesToPeriods(values),
					provider: VideoProviderName.parse(providerValue),
				},
			});
		} else {
			addErrorToast("please select a game & provider");
		}
	};

	const defaults: DefaultFormValues = {
		posterTime: "00:00",
		...periods,
	};

	// Not Sent tab = waiting-for-approval: not yet approved and not soft-deleted.
	// Match the list: block delete while the file is still mid-ingest.
	const videoIsDeleted = isVideoDeleted(video);
	const canShowDelete = video.metadataApprovedAt == null && videoIsDeleted === false;
	const fileReady = isFileReady(video.apiStatus);
	const canDelete = canShowDelete && fileReady;
	const playbackSource = video.link ?? "";
	const streamUnavailable = isVideoPlaybackUnavailable(video);
	const playbackUnavailable = streamUnavailable || playbackFailed;

	const tabItems = [
		{ key: VideoPageTab.Setup, label: _("admin.video.tabs.setup") },
		{ key: VideoPageTab.Info, label: _("admin.video.tabs.info") },
	];

	const onTabClick = (key: string) => {
		if (isVideoPageTab(key) === false) {
			return;
		}
		const nextParams = new URLSearchParams(searchParams);
		nextParams.set("tab", key);
		setSearchParams(nextParams, { replace: true });
	};

	useEffect(() => {
		setPlaybackFailed(false);
		setPlaybackReady(false);
	}, [video.id, playbackSource]);

	useEffect(() => {
		if (
			shouldStartPlaybackReadyTimeout({
				isSetupTabVisible: activeTab === VideoPageTab.Setup,
				streamUnavailable,
				playbackFailed,
				playbackReady,
				playbackSource,
			}) === false
		) {
			return;
		}

		const timeoutId = window.setTimeout(() => {
			setPlaybackFailed(true);
		}, PLAYBACK_READY_TIMEOUT_MS);

		return () => {
			window.clearTimeout(timeoutId);
		};
	}, [activeTab, video.id, playbackSource, streamUnavailable, playbackFailed, playbackReady]);

	useEffect(() => {
		if (activeTab === VideoPageTab.Setup) {
			return;
		}
		player.current?.pause();
	}, [activeTab]);

	return (
		<div className="videoMetadataEditor">
			<div className="VideoMetadataHeading-container">
				<div className="VideoMetadataHeading">
					<div className="VideoMetadataHeading-nameRow">
						<h1>
							{`${_("matchSheet.table.playerName")}:`} {video.name}
						</h1>
						<RenameVideoButton
							videoId={video.id}
							currentName={video.name}
							className="videoRenameButton"
							label={<span className="icon-new icon-white icon-crayon icon-16" aria-hidden />}
						/>
						{canShowDelete ? (
							<RowActionButton
								icon={rowActionIcons.delete}
								label={_("admin.video.actions.delete")}
								onClick={() => setShowDeleteModal(true)}
								disabled={fileReady === false}
								disabledReason={fileNotReadyReason(video.apiStatus)}
							/>
						) : null}
					</div>
				</div>
				<div className="BacktoVideoMetadata">
					<span className="icon-new icon-24 icon-dark-lavendar icon-admin-arrow-new rotate-180" />
					<Link to={videoListBackUrl}>{_("admin.video.backToVideoList")}</Link>
				</div>
			</div>

			<div className="videoMetadataEditor-tabs">
				<Tabs items={tabItems} active={activeTab} onClick={onTabClick} />
			</div>

			<div className="videoMetadataEditor-setupTab" hidden={activeTab !== VideoPageTab.Setup}>
				<div className="Videoplayer-container">
					{streamUnavailable === true ? (
						<div className="Videoplayer-unavailable">{_("admin.video.player.unavailable")}</div>
					) : (
						<>
							{playbackFailed === true ? (
								<div className="Videoplayer-unavailable Videoplayer-unavailableOverlay">
									{_("admin.video.player.unavailable")}
								</div>
							) : null}
							<MediaPlayer
								ref={player}
								load="visible"
								src={{ src: playbackSource, type: "video/mp4" }}
								onTimeUpdate={({ currentTime }) => {
									setCurrentTime(currentTime);
								}}
								onCanPlay={() => {
									setPlaybackReady(true);
									setPlaybackFailed(false);
								}}
								onError={() => {
									setPlaybackFailed(true);
								}}
							>
								<MediaProvider />
								<DefaultVideoLayout icons={defaultLayoutIcons} />
							</MediaPlayer>
						</>
					)}
				</div>
				{playbackUnavailable === false && video.duration != null && video.duration !== "" && (
					<Timeline
						periods={filledPeriods}
						currentTime={currentTime}
						duration={timeToSeconds(parseTime(video.duration))}
						player={player.current}
					/>
				)}

				<div className="VideotimingDetails-container videoSetupLayout">
					<div className="VideotimingDetails">
						<MetadataForm onSubmit={onSubmit} player={player.current} defaults={defaults} onChange={setFilledPeriods} />
						<button
							disabled={video.metadata === "null"}
							type="button"
							className={clsx("videoMetadataEditor-approvalButton", { disabled: video.metadata === "null" })}
							onClick={() => {
								setVideoToApprove(video.id);
								setShowModal(true);
							}}
						>
							{video.metadataApprovedAt ? (
								<>{_("admin.videoUploads.metadataEditor.modalRevokeTitle")}</>
							) : (
								<>{_("admin.videoUploads.metadataEditor.btnApprove.label")}</>
							)}
						</button>
					</div>
					<div className="gameProvider-container">
						<div className="videoProvider-container">
							<div>{`${_("admin.video.selectVideoProvider")} *`}</div>
							<Select
								options={providersDropdownOptions}
								isClearable={isVeoProvider === false}
								isDisabled={isVeoProvider}
								value={isVeoProvider ? veoProviderOption : videoProvider}
								onChange={setVideoProvider}
							/>
						</div>
						<div className="videoProvider-switch">
							{_("admin.video.gamesWithoutVideo")}
							<ToggleSwitch
								handleChange={() => {
									setShowGamesWithVideo(!showGamesWithVideo);
								}}
							/>
							{_("admin.video.gamesWithVideo")}
						</div>
						<GameSelectComponent metadata={metadata} withVideo={showGamesWithVideo} onGameSelect={onGameSelect} />
					</div>
				</div>
			</div>
			{activeTab === VideoPageTab.Info ? <MatchAndVideoInfoTab video={video} /> : null}

			<Modal
				show={showModal}
				onClose={() => setShowModal(false)}
				size="sm"
				title={
					video.metadataApprovedAt
						? _("admin.videoUploads.metadataEditor.modalRevokeTitle")
						: _("admin.videoUploads.metadataEditor.modalApproveTitle")
				}
			>
				<ApprovalModal
					selectedGame={selectedGame}
					videoId={videoToApprove ?? 0}
					approved={!!video.metadataApprovedAt}
					close={() => setShowModal(false)}
				/>
			</Modal>
			{canDelete ? (
				<Modal show={showDeleteModal} onClose={() => setShowDeleteModal(false)} size="sm" title={_("admin.video.actions.delete")}>
					<DeleteModal
						videoId={video.id ?? 0}
						close={() => setShowDeleteModal(false)}
						navigateAfterDelete="/video-uploads/not-sent"
					/>
				</Modal>
			) : null}
		</div>
	);
}
